
import { useEffect, useRef, useState } from 'react';

export default function Home() {
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const sectionsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const technologies = [
    {
      title: "Artificial Intelligence & Machine Learning",
      description: "Revolutionizing industries through intelligent automation, predictive analytics, and neural networks that transform raw data into actionable insights.",
      features: ["Deep Learning Algorithms", "Natural Language Processing", "Computer Vision", "Predictive Analytics"],
      icon: "ri-robot-line",
      gradient: "from-purple-600 via-blue-600 to-cyan-500",
      bgImage: "https://readdy.ai/api/search-image?query=Futuristic%20artificial%20intelligence%20neural%20network%20visualization%20with%20glowing%20blue%20and%20purple%20data%20streams%20flowing%20through%20digital%20brain%20pathways%20in%20a%20high-tech%20laboratory%20environment%20with%20holographic%20displays%20and%20advanced%20computing%20interfaces&width=1920&height=1080&seq=ai-bg-001&orientation=landscape"
    },
    {
      title: "Quantum Computing Solutions",
      description: "Harnessing quantum mechanics to solve complex computational problems exponentially faster than classical computers, opening new frontiers in cryptography and simulation.",
      features: ["Quantum Algorithms", "Cryptographic Security", "Molecular Simulation", "Optimization Problems"],
      icon: "ri-cpu-line",
      gradient: "from-emerald-500 via-teal-600 to-cyan-600",
      bgImage: "https://readdy.ai/api/search-image?query=Quantum%20computing%20laboratory%20with%20glowing%20quantum%20processors%20suspended%20in%20geometric%20crystalline%20structures%20surrounded%20by%20flowing%20energy%20patterns%20and%20holographic%20quantum%20state%20visualizations%20in%20emerald%20and%20teal%20colors&width=1920&height=1080&seq=quantum-bg-002&orientation=landscape"
    },
    {
      title: "Blockchain & Distributed Systems",
      description: "Building decentralized infrastructure that ensures transparency, security, and trust in digital transactions across global networks.",
      features: ["Smart Contracts", "Decentralized Applications", "Consensus Mechanisms", "Digital Identity"],
      icon: "ri-links-line",
      gradient: "from-orange-500 via-red-500 to-pink-600",
      bgImage: "https://readdy.ai/api/search-image?query=Blockchain%20network%20visualization%20showing%20interconnected%20nodes%20and%20data%20blocks%20floating%20in%20space%20with%20golden%20orange%20and%20red%20energy%20connections%20forming%20complex%20geometric%20patterns%20in%20a%20futuristic%20digital%20environment&width=1920&height=1080&seq=blockchain-bg-003&orientation=landscape"
    },
    {
      title: "Extended Reality (AR/VR/MR)",
      description: "Creating immersive digital experiences that blend physical and virtual worlds, transforming how we interact with information and each other.",
      features: ["Spatial Computing", "Haptic Feedback", "3D Visualization", "Immersive Interfaces"],
      icon: "ri-vr-line",
      gradient: "from-indigo-500 via-purple-600 to-pink-500",
      bgImage: "https://readdy.ai/api/search-image?query=Virtual%20reality%20environment%20with%20floating%20holographic%20interfaces%20and%203D%20geometric%20shapes%20in%20vibrant%20purple%20and%20pink%20colors%20with%20users%20wearing%20VR%20headsets%20interacting%20with%20digital%20objects%20in%20futuristic%20space&width=1920&height=1080&seq=ar-vr-bg-004&orientation=landscape"
    },
    {
      title: "Internet of Things (IoT) Ecosystem",
      description: "Connecting billions of smart devices to create intelligent environments that respond to human needs and optimize resource utilization.",
      features: ["Edge Computing", "Sensor Networks", "Real-time Analytics", "Smart Automation"],
      icon: "ri-wifi-line",
      gradient: "from-green-500 via-emerald-600 to-teal-500",
      bgImage: "https://readdy.ai/api/search-image?query=Connected%20IoT%20devices%20and%20sensors%20in%20a%20smart%20city%20environment%20with%20glowing%20green%20data%20connections%20linking%20buildings%20vehicles%20and%20infrastructure%20creating%20a%20network%20of%20intelligent%20systems&width=1920&height=1080&seq=iot-bg-005&orientation=landscape"
    },
    {
      title: "5G & Edge Computing",
      description: "Ultra-low latency networks enabling real-time processing at the edge, powering autonomous systems and instant global connectivity.",
      features: ["Ultra-Low Latency", "Massive IoT Connectivity", "Network Slicing", "Mobile Edge Computing"],
      icon: "ri-signal-tower-line",
      gradient: "from-blue-500 via-indigo-600 to-purple-500",
      bgImage: "https://readdy.ai/api/search-image?query=5G%20network%20towers%20and%20edge%20computing%20infrastructure%20with%20blue%20and%20purple%20electromagnetic%20waves%20flowing%20through%20fiber%20optic%20cables%20and%20wireless%20signals%20in%20a%20high-tech%20communication%20landscape&width=1920&height=1080&seq=5g-edge-bg-006&orientation=landscape"
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=Futuristic%20technology%20cityscape%20with%20holographic%20displays%20floating%20geometric%20shapes%20and%20digital%20neural%20networks%20in%20deep%20space%20with%20stars%20and%20nebula%20featuring%20advanced%20technological%20interfaces%20and%20glowing%20data%20streams&width=1920&height=1080&seq=hero-bg-main&orientation=landscape')`,
            transform: `translateY(${scrollY * 0.5}px)`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60" />
        
        {/* 3D Floating Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-cyan-400 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                transform: `translate(${(mousePosition.x - window.innerWidth / 2) * (i * 0.01)}px, ${(mousePosition.y - window.innerHeight / 2) * (i * 0.01)}px)`,
                animationDelay: `${i * 0.1}s`,
                opacity: Math.random() * 0.8 + 0.2
              }}
            />
          ))}
        </div>

        <div className="relative z-10 text-center max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="transform hover:scale-105 transition-transform duration-700">
            <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-bold mb-8 bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent animate-pulse">
              FUTURE TECHNOLOGIES
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl mb-12 text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Pioneering tomorrow's innovations today through cutting-edge research and development in emerging technologies
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <button className="group relative px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full text-lg font-semibold transition-all duration-300 hover:scale-110 hover:shadow-2xl hover:shadow-cyan-500/50">
                <span className="relative z-10">Explore Technologies</span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </button>
              <button className="group px-8 py-4 border-2 border-cyan-400 rounded-full text-lg font-semibold transition-all duration-300 hover:bg-cyan-400 hover:text-black hover:scale-110">
                Watch Demo
              </button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-3xl text-cyan-400"></i>
        </div>
      </section>

      {/* Technology Sections */}
      {technologies.map((tech, index) => (
        <section 
          key={index}
          ref={el => sectionsRef.current[index] = el}
          className="relative min-h-screen py-20 flex items-center"
        >
          {/* Parallax Background */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-fixed"
            style={{
              backgroundImage: `url('${tech.bgImage}')`,
              transform: `translateY(${scrollY * (index % 2 === 0 ? 0.3 : -0.3)}px)`
            }}
          />
          <div className={`absolute inset-0 bg-gradient-to-r ${tech.gradient} opacity-20`} />
          <div className="absolute inset-0 bg-black/40" />

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className={`grid lg:grid-cols-2 gap-12 xl:gap-16 items-center ${index % 2 === 0 ? '' : 'lg:grid-flow-col-dense'}`}>
              
              {/* Content */}
              <div className={`space-y-8 ${index % 2 === 0 ? 'lg:pr-8' : 'lg:pl-8 lg:col-start-2'}`}>
                <div className="transform hover:scale-105 transition-transform duration-500">
                  <div className="flex items-center mb-6">
                    <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${tech.gradient} flex items-center justify-center mr-6 group-hover:rotate-180 transition-transform duration-500`}>
                      <i className={`${tech.icon} text-2xl text-white`}></i>
                    </div>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                      {tech.title}
                    </h2>
                  </div>
                  
                  <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 mb-8 leading-relaxed">
                    {tech.description}
                  </p>

                  <div className="grid sm:grid-cols-2 gap-4">
                    {tech.features.map((feature, featureIndex) => (
                      <div 
                        key={featureIndex}
                        className="group p-6 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl hover:bg-white/20 hover:scale-105 transition-all duration-300 cursor-pointer"
                      >
                        <div className="flex items-center">
                          <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${tech.gradient} mr-3 group-hover:animate-pulse`} />
                          <span className="text-sm sm:text-base lg:text-lg font-medium text-white group-hover:text-cyan-300 transition-colors duration-300">
                            {feature}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button className={`group mt-8 px-8 py-4 bg-gradient-to-r ${tech.gradient} rounded-full text-lg font-semibold transition-all duration-300 hover:scale-110 hover:shadow-2xl whitespace-nowrap`}>
                    Learn More
                    <i className="ri-arrow-right-line ml-2 group-hover:translate-x-2 transition-transform duration-300"></i>
                  </button>
                </div>
              </div>

              {/* 3D Visual Element */}
              <div className={`relative ${index % 2 === 0 ? 'lg:pl-8' : 'lg:pr-8 lg:col-start-1 lg:row-start-1'}`}>
                <div className="relative group cursor-pointer">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-y-6 group-hover:skew-y-6 transition-transform duration-700"></div>
                  
                  {/* 3D Card Container */}
                  <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg border border-white/20 rounded-3xl p-8 lg:p-12 transform hover:rotateY-12 transition-all duration-700 hover:scale-105 hover:shadow-2xl">
                    <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-transparent rounded-3xl transform rotate-1 group-hover:rotate-3 transition-transform duration-500"></div>
                    
                    {/* Floating 3D Elements */}
                    <div className="relative z-10">
                      {[...Array(12)].map((_, i) => (
                        <div
                          key={i}
                          className={`absolute w-3 h-3 bg-gradient-to-r ${tech.gradient} rounded-full animate-pulse`}
                          style={{
                            left: `${20 + (i % 4) * 20}%`,
                            top: `${20 + Math.floor(i / 4) * 25}%`,
                            transform: `translate(${Math.sin(scrollY * 0.01 + i) * 20}px, ${Math.cos(scrollY * 0.01 + i) * 15}px)`,
                            animationDelay: `${i * 0.2}s`,
                            opacity: 0.6 + Math.sin(scrollY * 0.01 + i) * 0.4
                          }}
                        />
                      ))}
                      
                      {/* Central 3D Icon */}
                      <div className="relative z-20 flex items-center justify-center h-64 lg:h-80">
                        <div className={`w-32 h-32 lg:w-40 lg:h-40 rounded-full bg-gradient-to-r ${tech.gradient} flex items-center justify-center transform group-hover:rotate-180 transition-transform duration-1000 hover:scale-110`}>
                          <i className={`${tech.icon} text-6xl lg:text-7xl text-white drop-shadow-2xl`}></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section Divider */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
        </section>
      ))}

      {/* Final CTA Section */}
      <section className="relative py-32 bg-gradient-to-br from-black via-gray-900 to-black">
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                opacity: Math.random() * 0.8 + 0.2
              }}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-8 bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent">
            Ready to Transform Your Future?
          </h2>
          <p className="text-xl sm:text-2xl lg:text-3xl text-gray-300 mb-12 leading-relaxed">
            Partner with us to harness these cutting-edge technologies and revolutionize your industry
          </p>
          <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
            <button className="group relative px-12 py-6 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full text-xl font-semibold transition-all duration-300 hover:scale-110 hover:shadow-2xl hover:shadow-cyan-500/50 whitespace-nowrap">
              <span className="relative z-10">Start Your Journey</span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </button>
            <button className="group px-12 py-6 border-2 border-cyan-400 rounded-full text-xl font-semibold transition-all duration-300 hover:bg-cyan-400 hover:text-black hover:scale-110 whitespace-nowrap">
              Contact Our Experts
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
